const ZA_TIME_ZONE = 'Africa/Johannesburg'

function hasExplicitTimezone(value: string) {
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value.trim())
}

function normalizeIsoLike(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return trimmed
  if (trimmed.includes('T')) return trimmed
  return trimmed.replace(' ', 'T')
}

function asDate(value: string): Date {
  const normalized = normalizeIsoLike(value)
  const parsed = new Date(normalized)
  if (!Number.isNaN(parsed.getTime())) return parsed

  if (!hasExplicitTimezone(normalized)) {
    const asZa = new Date(`${normalized}+02:00`)
    if (!Number.isNaN(asZa.getTime())) return asZa
  }

  return parsed
}

export function isTimezoneAwareIso(value: string) {
  const normalized = normalizeIsoLike(value)
  return /\dT\d/.test(normalized) && hasExplicitTimezone(normalized)
}

export function fromZaLocalToUtcIso(day: string, time: string) {
  const hhmm = time?.trim() || '00:00'
  const date = new Date(`${day}T${hhmm}:00+02:00`)
  return date.toISOString()
}

function formatInZa(date: Date, opts: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ZA_TIME_ZONE,
    hour12: false,
    ...opts,
  }).format(date)
}

export function fromUtcIsoToZaParts(iso: string): { day: string; time: string } {
  const date = asDate(iso)
  if (Number.isNaN(date.getTime())) return { day: '', time: '' }

  const day = formatInZa(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const time = formatInZa(date, {
    hour: '2-digit',
    minute: '2-digit',
  })

  return { day, time }
}

export function toZaDateKey(iso: string): string {
  return fromUtcIsoToZaParts(iso).day
}

export function toZaTime(iso: string): string {
  return fromUtcIsoToZaParts(iso).time
}

export function toZaNowDateKey(now = new Date()): string {
  return formatInZa(now, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

export function normalizeToUtcIso(value: string): string {
  const date = asDate(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toISOString()
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/** '2026-06-27' + '09:00' → '27 Jun · 09:00' (year shown when not current). */
export function formatBookedLabel(date: string, time: string): string {
  const [year, month, day] = date.split('-').map(Number)
  if (!year || !month || !day) {
    return time ? `${date} · ${time}` : date
  }
  const dayLabel = `${day} ${MONTHS_SHORT[month - 1] ?? ''}`
  const withYear =
    year === new Date().getFullYear() ? dayLabel : `${dayLabel} ${year}`
  return time ? `${withYear} · ${time}` : withYear
}
